import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { isLightModeEnabled } from '@/config/featureFlags';

/** User-selectable theme preference. 'auto' follows the OS/browser setting. */
export type ThemePreference = 'light' | 'dark' | 'auto';

/** The theme actually applied to the page once 'auto' is resolved. */
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'crm-intelligence-theme';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'auto';
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
}

function readStoredPreference(): ThemePreference {
  // Light/Auto aren't styled yet (see featureFlags.ts), so Dark is forced
  // for everyone until VITE_ENABLE_LIGHT_MODE is turned on. The original
  // stored-preference logic below is kept intact for when that happens.
  if (!isLightModeEnabled) return 'dark';
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isThemePreference(stored) ? stored : 'dark';
}

export interface ThemeContextValue {
  /** The user's chosen preference: 'light' | 'dark' | 'auto'. */
  theme: ThemePreference;
  /** The concrete theme currently applied ('auto' resolved to light/dark). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Site-wide Light/Dark/Auto theme, mirroring the switcher on getbootstrap.com.
 * Defaults to 'dark' (the product theme) and persists the user's explicit
 * choice to localStorage. Applies the resolved theme via Bootstrap 5.3's
 * `data-bs-theme` attribute on <html>, which every Bootstrap component and
 * utility class already respects.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(readStoredPreference);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = () => setSystemTheme(media.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === 'auto' ? systemTheme : theme;

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: ThemePreference) => {
    // Defensive: the Profile page disables the Light/Auto controls, but keep
    // this guard so Dark stays authoritative until light mode is re-enabled.
    if (!isLightModeEnabled && next !== 'dark') return;
    setThemeState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
